/* SALES OP WEEKLY REPORT · CONFIG (2026-10-25). Max: "file config tôi nghĩ nên là 1 tab riêng việc đổi khi có điền chỉnh
   hoặc bổ sung giá trị vào để dùng khi chạy code á". The three sheets of 00_Config.xlsx the job reads, as tables:
   items (Item Code -> Category, Purchase Price), customers (Buyer -> Helinox Entity, for AR) and stock locations (ID ->
   name, for SAP). Locked until "Edit the table", then every cell, a new line and a removal are a draft that ONE Save
   writes (backend/sopWeekly.js PUT /config/:sheet, which checks the whole sheet, copies the file first and refuses when
   the file moved since it was read). The job's logic is not touched — it reads the same workbook it always has.
   UPLOAD (Max, 2026-10-28: "user sẽ tải file xuống chỉnh sửa và upload lên lại"): the edited 00_Config.xlsx comes back,
   the server compares it with the current file by key and shows what would change — nothing is written until Apply.
   Rows of the current file missing from the upload are removed only when ticked (UploadPreview below). */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, Pencil, Plus, Trash2, Undo2, Upload, X } from 'lucide-react';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import useRowWindow from './useRowWindow';
import { BACKEND_URL } from './backendConfig';

const API = `${BACKEND_URL}/api/sop-weekly`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtWhen = (v) => {
  if (!v) return '';
  const vn = new Date(new Date(v).getTime() + 7 * 3600 * 1000).toISOString();
  return `${vn.slice(8, 10)}/${MONTHS[Number(vn.slice(5, 7)) - 1]}/${vn.slice(0, 4)} ${vn.slice(11, 16)}`;
};
const call = async (method, path, body) => {
  const r = await fetch(`${API}${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
  return json;
};
const txt = (v) => (v == null ? '' : String(v));
const FIELD_LABEL = { code: 'Item Code', product: 'Product', name: 'Name', category: 'Category', price: 'Purchase Price', buyer: 'Buyer', entity: 'Helinox Entity' };

/* what an uploaded file would change, sheet by sheet; Apply writes it */
function UploadPreview({ t, up, busy, onApply, onCancel }) {
  const [removeMissing, setRemoveMissing] = useState(false);
  const plans = Object.values(up.plan || {});
  const nMissing = plans.reduce((n, p) => n + p.missing.length, 0);
  const nWrite = plans.reduce((n, p) => n + p.changed.length + p.added.length, 0) + (removeMissing ? nMissing : 0);
  const shownVal = (v) => (v == null || v === '' ? '—' : String(v));
  return (
    <div className="sw-up" data-sw-upload="1">
      <div className="sw-up-head">
        <b>{t('xSopw.upTitle', { file: up.file })}</b>
        <span className="sw-grow" />
        <button type="button" className="pa-btn small" onClick={onCancel} disabled={busy}><X size={13} /> {t('xSopw.cfgCancel')}</button>
      </div>
      {up.problems.length > 0 ? (
        <>
          <p className="sw-hint sw-neg">{t('xSopw.upProblems', { n: up.problems.length })}</p>
          <ul className="sw-up-problems" data-sw-problems="1">{up.problems.slice(0, 30).map((x) => <li key={x}>{x}</li>)}</ul>
          {up.problems.length > 30 && <p className="sw-hint">{t('xSopw.upMore', { n: up.problems.length - 30 })}</p>}
        </>
      ) : up.nothing ? (
        <p className="sw-hint" data-sw-nothing="1">{t('xSopw.upNothing')}</p>
      ) : (
        <>
          <p className="sw-hint">{t('xSopw.upHint')}</p>
          {plans.filter((p) => p.changed.length || p.added.length || p.missing.length).map((p) => (
            <div key={p.sheet} className="sw-up-sheet">
              <h4>{p.sheet} <span className="sw-sub">{t('xSopw.upCounts', { c: p.changed.length, a: p.added.length, m: p.missing.length })}</span></h4>
              <table className="pa-table sw-up-table">
                <tbody>
                  {p.changed.map((c) => (
                    <tr key={`c${c.row}`} className="sw-up-changed">
                      <td><em className="sw-chip">{t('xSopw.upChanged')}</em></td>
                      <td className="sw-code">{c.key}</td>
                      <td>{c.diffs.map((d) => <div key={d.field}>{FIELD_LABEL[d.field] || d.field}: <s>{shownVal(d.from)}</s> → <b>{shownVal(d.to)}</b></div>)}</td>
                    </tr>
                  ))}
                  {p.added.map((a, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`a${i}`} className="sw-up-added">
                      <td><em className="sw-chip ok">{t('xSopw.upAdded')}</em></td>
                      <td className="sw-code">{shownVal(a.code ?? a.buyer ?? a.id)}</td>
                      <td>{Object.entries(a).filter(([k]) => !['code', 'id'].includes(k)).map(([k, v]) => `${FIELD_LABEL[k] || k}: ${shownVal(v)}`).join(' · ')}</td>
                    </tr>
                  ))}
                  {p.missing.map((m) => (
                    <tr key={`m${m.row}`} className={`sw-up-missing${removeMissing ? ' on' : ''}`}>
                      <td><em className="sw-chip miss">{removeMissing ? t('xSopw.upRemoved') : t('xSopw.upNotInFile')}</em></td>
                      <td className="sw-code">{m.key}</td>
                      <td>{Object.entries(m).filter(([k]) => !['row', 'key'].includes(k)).map(([k, v]) => `${FIELD_LABEL[k] || k}: ${shownVal(v)}`).join(' · ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {nMissing > 0 && (
            <label className="sw-up-remove">
              <input type="checkbox" checked={removeMissing} onChange={(e) => setRemoveMissing(e.target.checked)} data-sw-remove="1" />
              {t('xSopw.upRemoveMissing', { n: nMissing })}
            </label>
          )}
          <div className="sw-up-foot">
            <button type="button" className="pa-btn small primary" disabled={busy || !nWrite} onClick={() => onApply(removeMissing)} data-sw-apply="1">
              {busy ? t('xSopw.upApplying') : t('xSopw.upApply', { n: nWrite })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
const blank = (v) => v == null || String(v).trim() === '';
const ROW_H = 38;

/* each sheet's columns, in the workbook's own words; `kind` decides the box drawn while editing */
const SPECS = {
  item: {
    sheet: '03_PD-Category-Price',
    cols: [
      { key: 'code', label: 'Item Code', kind: 'code', w: 130 },
      { key: 'product', label: 'Product', w: 360 },
      { key: 'name', label: 'Name', w: 300 },
      { key: 'category', label: 'Category', kind: 'category', w: 140 },
      { key: 'price', label: 'Purchase Price', kind: 'price', w: 130, num: true },
    ],
  },
  customer: {
    sheet: '01_Customer',
    cols: [
      { key: 'buyer', label: 'Buyer', w: 360 },
      { key: 'entity', label: 'Helinox Entity', kind: 'entity', w: 180 },
    ],
  },
  location: {
    sheet: '02_Stock-Location',
    cols: [
      { key: 'id', label: 'Stock Location ID', kind: 'code', w: 160 },
      { key: 'name', label: 'Stock Location Name', w: 300 },
    ],
  },
};

export default function SopWeeklyConfig({ t, edit, flash, running }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sheet, setSheet] = useState(() => new URLSearchParams(window.location.search).get('sheet') || 'item');
  const [editing, setEditing] = useState(false);
  const [changes, setChanges] = useState({}); // row -> { field: value }
  const [adds, setAdds] = useState([]); // [{ tmp, ...fields }]
  const [dels, setDels] = useState(() => new Set());
  const [q, setQ] = useState('');
  const [needOnly, setNeedOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [up, setUp] = useState(null);       // the uploaded file's comparison, until Apply or Cancel
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setErr(null);
    return call('GET', '/config').then(setData).catch((e) => setErr(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const spec = SPECS[sheet];
  const rows = useMemo(() => data?.[sheet]?.rows || [], [data, sheet]);
  const val = (r, k) => (changes[r.row] && k in changes[r.row] ? changes[r.row][k] : r[k]);
  const needs = (r) => sheet === 'item' && (blank(val(r, 'category')) || blank(val(r, 'price')));
  const needCount = sheet === 'item' ? rows.filter(needs).length : 0;
  const dupCodes = useMemo(() => {
    if (sheet !== 'item') return new Set();
    const c = new Map();
    for (const r of rows) { const k = txt(r.code).trim(); c.set(k, (c.get(k) || 0) + 1); }
    return new Set([...c].filter(([, n]) => n > 1).map(([k]) => k));
  }, [rows, sheet]);
  const entities = useMemo(() => [...new Set((data?.customer?.rows || []).map((r) => txt(r.entity)).filter(Boolean))].sort(), [data]);

  const columns = spec.cols.map((c) => ({ ...c, get: (r) => (c.num ? (blank(val(r, c.key)) ? '' : Number(val(r, c.key))) : txt(val(r, c.key))) }));
  const pre = rows.filter((r) => {
    if (needOnly && !needs(r)) return false;
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return spec.cols.some((c) => txt(val(r, c.key)).toLowerCase().includes(s));
  });
  const tools = useColumnTools(pre, columns);
  const shown = tools.shown;
  const [boxRef, win] = useRowWindow({ rowHeight: ROW_H, total: shown.length, resetKey: `${sheet}|${q}|${needOnly}` });

  const pending = Object.keys(changes).filter((row) => Object.entries(changes[row]).some(([k, v]) => {
    const r = rows.find((x) => String(x.row) === String(row));
    return r && txt(v).trim() !== txt(r[k]).trim();
  }));
  const nChanges = pending.filter((row) => !dels.has(Number(row))).length + adds.length + dels.size;

  const reset = () => { setChanges({}); setAdds([]); setDels(new Set()); };
  const pickSheet = (k) => {
    if (editing) return;
    setSheet(k); setQ(''); setNeedOnly(false); tools.clear();
    const u = new URL(window.location.href); u.searchParams.set('sheet', k); window.history.replaceState(null, '', u);
  };
  const setCell = (r, k, v) => setChanges((d) => ({ ...d, [r.row]: { ...d[r.row], [k]: v } }));
  const setAdd = (tmp, k, v) => setAdds((list) => list.map((a) => (a.tmp === tmp ? { ...a, [k]: v } : a)));
  const addLine = () => setAdds((list) => [{ tmp: Date.now() + Math.random(), ...Object.fromEntries(spec.cols.map((c) => [c.key, ''])) }, ...list]);
  const toggleDel = (row) => setDels((s) => { const n = new Set(s); if (n.has(row)) n.delete(row); else n.add(row); return n; });

  const save = async () => {
    setBusy(true);
    try {
      const updates = pending.filter((row) => !dels.has(Number(row))).map((row) => ({ row: Number(row), ...changes[row] }));
      const r = await call('PUT', `/config/${sheet}`, {
        baseMtime: data.mtime,
        updates,
        adds: adds.map(({ tmp, ...a }) => a),
        deletes: [...dels],
      });
      flash(t('xSopw.cfgSaved', { sheet: spec.sheet, n: nChanges, backup: r.backup }));
      reset(); setEditing(false);
      await load();
    } catch (e) { flash(e.message, true); }
    setBusy(false);
  };

  const sendFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/config-upload?name=${encodeURIComponent(file.name)}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/octet-stream' }, body: file,
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`);
      setUp(json);
    } catch (e) { flash(e.message, true); }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const applyUpload = async (removeMissing) => {
    setBusy(true);
    try {
      const r = await call('POST', `/config-upload/${up.token}/apply`, { removeMissing });
      flash(t('xSopw.upDone', { what: r.done.join('; '), backup: r.backup }));
      setUp(null);
      await load();
    } catch (e) { flash(e.message, true); setUp(null); }
    setBusy(false);
  };

  const box = (c, value, onChange, isNew) => {
    const common = { value: txt(value), onChange: (e) => onChange(e.target.value), 'aria-label': c.label };
    if (c.kind === 'category') {
      return (
        <select {...common} className={blank(value) ? 'sw-cfg-need' : ''}>
          <option value="">—</option>
          {(data?.categories || []).map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
      );
    }
    if (c.kind === 'price') return <input {...common} inputMode="decimal" placeholder="0.00" className={blank(value) ? 'sw-cfg-need num' : 'num'} onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))} />;
    if (c.kind === 'entity') return <input {...common} list="sw-cfg-entities" />;
    if (c.kind === 'code' && !isNew) return <span className="sw-code">{txt(value)}</span>; // a row's key: correct it by removing the row and adding a new one
    return <input {...common} />;
  };
  const show = (c, r) => {
    const v = val(r, c.key);
    if (c.kind === 'price') return blank(v) ? <span className="sw-cfg-missing">{t('xSopw.cfgMissing')}</span> : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    if (c.kind === 'category' && blank(v)) return <span className="sw-cfg-missing">{t('xSopw.cfgMissing')}</span>;
    if (c.kind === 'code') return <span className="sw-code">{txt(v)}{dupCodes.has(txt(v).trim()) && <em className="sw-chip miss" title={t('xSopw.cfgDupHint')}>{t('xSopw.cfgDup')}</em>}</span>;
    return txt(v);
  };
  const width = 70 + spec.cols.reduce((s, c) => s + c.w, 0) + (editing ? 60 : 0);

  if (err) return <div className="pa-section"><p className="sw-hint sw-neg">{err}</p></div>;
  if (!data) return <div className="pa-section"><p className="sw-hint">{t('xSopw.loading')}</p></div>;

  return (
    <div className="pa-section sw-cfg">
      <div className="pa-toolbar sw-repbar">
        <span className="sw-sub">{t('xSopw.cfgFile', { when: fmtWhen(data.modified) })}</span>
        <span className="sw-grow" />
        <a className="pa-btn small" href={`${API}/download/config`}><Download size={13} /> 00_Config.xlsx</a>
        {edit && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={(e) => sendFile(e.target.files?.[0])} data-sw-file="1" />
            <button type="button" className="pa-btn small primary" disabled={busy || running || editing || !!up}
              title={running ? t('xSopw.cfgRunning') : editing ? t('xSopw.cfgSaveFirst') : t('xSopw.upButtonHint')}
              onClick={() => fileRef.current?.click()} data-sw-upload-btn="1">
              <Upload size={13} /> {t('xSopw.upButton')}
            </button>
          </>
        )}
      </div>
      <p className="sw-hint">{t('xSopw.cfgHint')}</p>
      {up && <UploadPreview t={t} up={up} busy={busy} onApply={applyUpload} onCancel={() => setUp(null)} />}
      <div className="sw-cfg-tabs" role="tablist">
        {Object.entries(SPECS).map(([k, s]) => (
          <button key={k} type="button" role="tab" aria-selected={sheet === k} disabled={editing && sheet !== k}
            title={editing && sheet !== k ? t('xSopw.cfgSaveFirst') : s.sheet}
            className={`sw-cfg-tab${sheet === k ? ' active' : ''}`} onClick={() => pickSheet(k)}>
            {t(`xSopw.cfg_${k}`)} <em>{(data[k]?.rows || []).length}</em>
          </button>
        ))}
      </div>
      <p className="sw-hint sw-cfg-use">{t(`xSopw.cfgUse_${sheet}`)}</p>

      <div className="pa-toolbar">
        <input className="sw-cfg-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xSopw.cfgSearch')} />
        {sheet === 'item' && (
          <button type="button" className={`pa-btn small${needOnly ? ' primary' : ''}`} disabled={!needCount && !needOnly} onClick={() => setNeedOnly((v) => !v)}>
            {t('xSopw.cfgNeed', { n: needCount })}
          </button>
        )}
        {tools.active && <button type="button" className="pa-btn small" onClick={tools.clear}>{t('xSopw.cfgClear')}</button>}
        <span className="sw-sub">{t('xSopw.cfgCount', { n: shown.length, total: rows.length })}</span>
        <span className="sw-grow" />
        {edit && !editing && (
          <button type="button" className="pa-btn small primary" disabled={running} title={running ? t('xSopw.cfgRunning') : ''} onClick={() => setEditing(true)}>
            <Pencil size={13} /> {t('xSopw.cfgEdit')}
          </button>
        )}
        {editing && (
          <>
            <button type="button" className="pa-btn small" onClick={addLine}><Plus size={13} /> {t('xSopw.cfgAdd')}</button>
            <button type="button" className="pa-btn small" onClick={() => { reset(); setEditing(false); }}><X size={13} /> {t('xSopw.cfgCancel')}</button>
            <button type="button" className="pa-btn small primary" disabled={busy || !nChanges || running} onClick={save}>{t('xSopw.cfgSave', { n: nChanges })}</button>
          </>
        )}
      </div>
      {!edit && <p className="sw-hint">{t('xSopw.cfgViewOnly')}</p>}

      <datalist id="sw-cfg-entities">{entities.map((e) => <option key={e} value={e} />)}</datalist>
      <div className="sw-cfg-box" ref={boxRef}>
        <table className="pa-table sw-cfg-table" style={{ width }}>
          <colgroup><col style={{ width: 70 }} />{spec.cols.map((c) => <col key={c.key} style={{ width: c.w }} />)}{editing && <col style={{ width: 60 }} />}</colgroup>
          <thead>
            <tr>
              <th title={t('xSopw.cfgRowHint')}>{t('xSopw.cfgRow')}</th>
              {columns.map((c) => <SortTh key={c.key} label={c.label} className={c.num ? 'num' : ''} {...tools.thProps(c.key)} />)}
              {editing && <th aria-label={t('xSopw.cfgRemove')} />}
            </tr>
          </thead>
          <tbody>
            {editing && adds.map((a) => (
              <tr key={a.tmp} className="sw-cfg-new" data-new="1">
                <td className="sw-quiet">{t('xSopw.cfgNew')}</td>
                {spec.cols.map((c) => <td key={c.key} className={c.num ? 'num' : ''}>{box(c, a[c.key], (v) => setAdd(a.tmp, c.key, v), true)}</td>)}
                <td><button type="button" className="pa-btn small" title={t('xSopw.cfgRemove')} onClick={() => setAdds((l) => l.filter((x) => x.tmp !== a.tmp))}><Trash2 size={13} /></button></td>
              </tr>
            ))}
            {win.padTop > 0 && <tr aria-hidden="true"><td colSpan={spec.cols.length + 2} style={{ height: win.padTop, padding: 0, border: 0 }} /></tr>}
            {shown.slice(win.first, win.first + win.count).map((r) => {
              const gone = dels.has(r.row);
              const moved = (k) => changes[r.row] && k in changes[r.row] && txt(changes[r.row][k]).trim() !== txt(r[k]).trim();
              return (
                <tr key={r.row} data-row={r.row} className={`${gone ? 'sw-cfg-gone' : ''}${needs(r) ? ' sw-cfg-needrow' : ''}`} style={{ height: ROW_H }}>
                  <td className="sw-quiet">{r.row}</td>
                  {spec.cols.map((c) => (
                    <td key={c.key} className={`${c.num ? 'num' : ''}${moved(c.key) ? ' sw-cfg-moved' : ''}`} title={moved(c.key) ? t('xSopw.cfgWas', { v: txt(r[c.key]) || '—' }) : undefined}>
                      {editing && !gone ? box(c, val(r, c.key), (v) => setCell(r, c.key, v), false) : show(c, r)}
                    </td>
                  ))}
                  {editing && (
                    <td><button type="button" className="pa-btn small" title={gone ? t('xSopw.cfgKeep') : t('xSopw.cfgRemove')} onClick={() => toggleDel(r.row)}>{gone ? <Undo2 size={13} /> : <Trash2 size={13} />}</button></td>
                  )}
                </tr>
              );
            })}
            {win.padBottom > 0 && <tr aria-hidden="true"><td colSpan={spec.cols.length + 2} style={{ height: win.padBottom, padding: 0, border: 0 }} /></tr>}
            {!shown.length && !adds.length && <tr><td colSpan={spec.cols.length + 2} className="sw-quiet">{t('xSopw.cfgNone')}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
